import { Grid } from '@mui/material';
import dayjs from 'dayjs';
import { useMemo } from 'react';
import { Section } from 'shared/components';
import { MetricWidget } from 'shared/components/metric/MetricWidget';
import { commify } from 'shared/utils/strings';

import { COST_DASHBOARD_VARIANT } from '../../constants';
import { useGetAllCostSummaries, useGetCostSummary, useGetRollupNamespaces } from '../../hooks';

const OverviewMetricSection: React.FC<{ variant?: COST_DASHBOARD_VARIANT }> = ({ variant }) => {
  const OVERVIEW_FILTER = {
    startDate: dayjs().startOf('month').format('YYYY-MM-DD'),
    endDate: dayjs().endOf('month').format('YYYY-MM-DD'),
    kind: 'ALL',
    type: 'ALL',
    area: variant === COST_DASHBOARD_VARIANT.ROLLUP ? 'rollup' : 'raas',
  };

  const { data: costSummary } = useGetCostSummary(OVERVIEW_FILTER);

  const { data: costSummaries } = useGetAllCostSummaries({
    ...OVERVIEW_FILTER,
    namespaceType: variant === COST_DASHBOARD_VARIANT.ROLLUP ? 'rollup' : 'raas',
  });

  const { data: namespaceList } = useGetRollupNamespaces({
    type: variant === COST_DASHBOARD_VARIANT.ROLLUP ? 'rollup' : 'raas',
  });

  const totalMonthCost = useMemo(() => {
    const sum = costSummary?.items?.reduce((sum, item) => sum + item.totalCost, 0) ?? 0;

    return Math.round(sum);
  }, [costSummary]);

  const avgCostPerRollup = useMemo(() => {
    const namespaceCount = namespaceList?.namespace?.length ?? 0;

    if (namespaceCount === 0) return 0;

    return Math.round(totalMonthCost / namespaceCount);
  }, [totalMonthCost, namespaceList]);

  const namespaceCosts = useMemo(() => {
    return costSummaries
      .filter(entry => (entry?.data?.items?.length ?? 0) > 0)
      .map(entry => ({
        name: entry.namespace,
        value: entry?.data?.items.reduce((sum, item) => sum + (item.totalCost ?? 0), 0),
      }));
  }, [costSummaries]);

  const highestMonthlyCost = useMemo(() => {
    const max = namespaceCosts.reduce((acc, entry) => {
      return Math.max(acc, entry.value ?? 0);
    }, 0);

    return Math.round(max);
  }, [namespaceCosts]);

  const costOverviewMetrics = useMemo(() => {
    return [
      {
        id: 1,
        title: 'Total Monthly Cost',
        value: `$${commify(totalMonthCost)}`,
        description:
          variant === COST_DASHBOARD_VARIANT.ROLLUP
            ? 'Total cost of all active rollups for the current billing month."'
            : 'The aggregate cost sum for all running namespaces within the current monthly billing period.',
      },
      {
        id: 2,
        title: variant === COST_DASHBOARD_VARIANT.ROLLUP ? 'Active Rollups' : 'Active Namespaces',
        value: namespaceList?.namespace?.length,
        description:
          variant === COST_DASHBOARD_VARIANT.ROLLUP
            ? 'Running rollups in testnet(stg env)/mainnet(prod env)'
            : 'The total count of general RaaS resource namespaces currently deployed.',
      },
      {
        id: 3,
        title:
          variant === COST_DASHBOARD_VARIANT.ROLLUP
            ? 'Avg Cost per Rollup'
            : 'Avg Cost each Namespace',
        value: `$${commify(avgCostPerRollup)}`,
        description:
          variant === COST_DASHBOARD_VARIANT.ROLLUP
            ? 'The average monthly cost per active rollup, derived by dividing the total monthly cost by the number of active rollups.'
            : 'The mean cost calculated by dividing Total Monthly Cost by Active Namespaces.',
      },
      {
        id: 4,
        title:
          variant === COST_DASHBOARD_VARIANT.ROLLUP
            ? 'Highest Rollup Cost'
            : 'Highest Namespace Cost',
        value: `$${commify(highestMonthlyCost)}`,
        description:
          variant === COST_DASHBOARD_VARIANT.ROLLUP
            ? 'The highest monthly cost incurred by an individual rollup instance.'
            : 'The maximum monthly cost incurred by any single namespace instance.',
      },
    ];
  }, [namespaceList, totalMonthCost, avgCostPerRollup, highestMonthlyCost, variant]);

  return (
    <Section
      renderContent={
        <Grid container>
          {costOverviewMetrics.map(({ description, id, title, value }) => {
            return (
              <MetricWidget key={id} label={title} tooltip={description} value={value} xs={3} />
            );
          })}
        </Grid>
      }
      title="Overview"
    />
  );
};

export default OverviewMetricSection;
